import React from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import Header from './Header';
import Footer from './Footer';

const Main = styled.main`
  min-height: calc(100vh - 80px - 100px); /* approx header + footer height */
  display: flex;
  flex-direction: column;
`;

const SITE_URL = 'https://buildingontruth.com';
const SITE_NAME = 'Building on Truth';
const DEFAULT_DESCRIPTION = 'Equipping Christians to engage AI, entrepreneurship, and business as a mission with biblical faithfulness and kingdom purpose.';
const DEFAULT_IMAGE = `${SITE_URL}/img/og-default.jpg`;
const DEFAULT_IMAGE_ALT = 'Building on Truth — Where Faith Meets Innovation';

const ROUTE_LABELS = {
    '/': 'Home',
    '/workshops': 'Workshops',
    '/events': 'Events',
    '/resources': 'Resources',
    '/about': 'About',
    '/contact': 'Contact',
    '/ministry-incubators': 'Ministry Incubators',
};

const buildBreadcrumb = (path) => {
    if (!path || path === '/' || path === '/404') return null;
    const label = ROUTE_LABELS[path] || path.replace(/^\//, '').replace(/-/g, ' ');
    return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
            { '@type': 'ListItem', position: 2, name: label, item: `${SITE_URL}${path}` },
        ],
    };
};

const Layout = ({
    children,
    title,
    description,
    image,
    imageAlt,
    path,
    type = 'website',
    keywords,
    noindex = false,
    jsonLd,
}) => {
    const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} | Where Faith Meets Innovation`;
    const metaDescription = description || DEFAULT_DESCRIPTION;
    const metaImage = image
        ? (image.startsWith('http') ? image : `${SITE_URL}${image.startsWith('/') ? '' : '/'}${image}`)
        : DEFAULT_IMAGE;
    const metaImageAlt = imageAlt || DEFAULT_IMAGE_ALT;
    const canonical = path
        ? `${SITE_URL}${path.startsWith('/') ? '' : '/'}${path}`
        : SITE_URL;

    const breadcrumb = buildBreadcrumb(path);
    const schemas = [
        ...(breadcrumb ? [breadcrumb] : []),
        ...(Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : []),
    ];

    return (
        <>
            <Helmet>
                <html lang="en" />
                <title>{fullTitle}</title>
                <meta name="description" content={metaDescription} />
                {keywords && <meta name="keywords" content={keywords} />}
                <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'} />
                <meta name="googlebot" content={noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1'} />
                <link rel="canonical" href={canonical} />

                {/* Open Graph */}
                <meta property="og:site_name" content={SITE_NAME} />
                <meta property="og:locale" content="en_US" />
                <meta property="og:type" content={type} />
                <meta property="og:url" content={canonical} />
                <meta property="og:title" content={fullTitle} />
                <meta property="og:description" content={metaDescription} />
                <meta property="og:image" content={metaImage} />
                <meta property="og:image:alt" content={metaImageAlt} />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />

                {/* Twitter / X */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={fullTitle} />
                <meta name="twitter:description" content={metaDescription} />
                <meta name="twitter:image" content={metaImage} />
                <meta name="twitter:image:alt" content={metaImageAlt} />

                {schemas.map((schema, i) => (
                    <script key={i} type="application/ld+json">
                        {JSON.stringify(schema)}
                    </script>
                ))}
            </Helmet>
            <Header />
            <Main>
                {children}
            </Main>
            <Footer />
        </>
    );
};

export default Layout;
